export default function LakeMaryDestinationsSection() {
  const stats = [
    {
      value: "42%",
      label: "of Lake Mary requests are business or corporate travel with 2–4 weeks notice",
    },
    {
      value: "3–4 weeks",
      label: "average notice for European conference and client meeting travel",
    },
    {
      value: "May–Aug",
      label: "peak student travel season — UCF study abroad and school program departures",
    },
    {
      value: "48-hour",
      label: "shortest corporate departure window we've accommodated for a Lake Mary executive",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "United Kingdom & Ireland",
      context: "The most common European destination for Lake Mary business travelers and students. London draws Deloitte consultants, AAA executives, and Verizon Business professionals for client meetings and conferences. Manchester, Glasgow, and Birmingham are accessible via SFB's TUI charter service. Dublin and Edinburgh attract leisure travelers from Lake Mary's affluent neighborhoods during summer breaks.",
      urgency: "Conference registration deadlines are typically 4–6 weeks before events, but flight bookings may happen only 2–3 weeks out",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Germany & France",
      context: "Frankfurt hosts the world's largest energy and automotive conferences — directly relevant to Mitsubishi Power and AAA's international operations. Paris and Monaco draw business travelers for the fashion, finance, and technology sectors. Lake Mary executives fly through MCO to Atlanta or New York, then connect to Frankfurt, Munich, or Paris for week-long business trips.",
      urgency: "European business travel is often booked 2–4 weeks in advance — insufficient for standard passport processing if an issue is discovered",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas & Caribbean",
      context: "The most common leisure destination for Lake Mary families — whether by cruise from Port Canaveral or by direct flight from MCO to Nassau, Montego Bay, or San Juan. Spring break and summer vacation bookings surge in January and February, creating a predictable passport discovery spike in March and April as families realize their children's five-year passports have expired.",
      urgency: "Spring break cruise and resort bookings happen in January — passport discoveries spike in February and March for Lake Mary families",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Spain & Italy",
      context: "UCF study abroad programs in Barcelona, Madrid, Rome, and Florence send hundreds of Central Florida students each summer and fall semester. Lake Mary and Oviedo families with college-aged children regularly discover passport issues when program registration deadlines arrive. These programs require passports valid through the return date, often 3–4 months after departure.",
      urgency: "Study abroad program deadlines are typically 2–3 months before departure — but students often wait until the last minute to check passport status",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Canada & Mexico",
      context: "Toronto and Vancouver draw Lake Mary business travelers for North American conferences and cross-border operations. Mexico — Cancun, Riviera Maya, and Los Cabos — is a staple spring break and winter vacation destination for Lake Mary families and UCF students. Both countries have specific entry requirements that Lake Mary travelers should verify before departure.",
      urgency: "Spring break Mexico bookings surge in January — passport validity discoveries peak in February for Lake Mary college students and families",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Japan & Asia-Pacific",
      context: "Mitsubishi Power and other technology firms in Lake Mary's I-4 corridor send engineers and executives to Tokyo, Osaka, and Singapore for technical conferences, supplier audits, and partnership meetings. These trips are typically planned 3–6 weeks in advance and require not just valid passports but often business visas with their own processing timelines.",
      urgency: "Asia-Pacific business travel often requires both passport renewal and visa processing — a combined timeline that can exceed 4–6 weeks",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Lake Mary Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Lake Mary
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Lake Mary travelers head to Europe for business and conferences, the Caribbean for family vacations, and Asia-Pacific for corporate operations. These are the destinations that create the most passport urgency for our Lake Mary clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout Central Florida</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Orlando", href: "/orlando" },
              { name: "Winter Park", href: "/winter-park" },
              { name: "Windermere", href: "/windermere" },
              { name: "Tampa", href: "/tampa" },
              { name: "Jacksonville", href: "/jacksonville" },
              { name: "West Palm Beach", href: "/west-palm-beach" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}